// MARK://dhk8/admin-shell/lib/clients/invoices
// Facturas / CFDI: lista, detalle, cancelar, factura global, clientes fiscales.
//   dhk8-erp-invoices (LECTURAS → GraphQL, F2.4 / ADR-0009):
//     query invoices(limit,status)  → { items, total, page, limit }  (tenant del JWT, RLS)
//     query invoice(id)             → Invoice (tenant del JWT, RLS)
//   dhk8-erp-invoices (ESCRITURAS → REST, hasta F8.x):
//     POST   /api/invoices/:id/cancel   body { satMotivo }
//   dhk8-fiscal-cfdi:
//     POST   /v1/cfdi/cancel           body { uuid, motivo } — cancela el CFDI timbrado
//   dhk8-erp-customers:
//     GET    /api/customers?tenantId=...                 → { items, total } (clientes fiscales)
//     POST   /api/customers            body { tenantId, legalName, rfc?, type }
//     PATCH  /api/customers/:id        body { legalName?, rfc? }
//     DELETE /api/customers/:id
//
// CONTRACT MISMATCHES:
//  - The Invoice row stores `customerId` (no embedded name/rfc) — the admin's
//    Invoice.customer/rfc are best-effort: customer = customerId, rfc = '' until the
//    UI joins the customer. Status maps ISSUED/SENT/PAID/PARTIALLY_PAID/OVERDUE →
//    STAMPED, CANCELLED → CANCELED, DRAFT → PENDING. uuid = cfdiUuid ?? ''.
//  - There's no "global invoice" (factura global) endpoint on either service yet —
//    generateGlobalInvoice falls back to a mock against the real backend (TODO).
//  - F0.3: el cliente fiscal ya persiste email/regimeCode/usoCfdi en el grafo
//    (erp-customers / dockethub_erp.customers). Round-trip real, ya no '' mock.
// Preview: mutable copy.
import { svcUrl, apiData, requireTenant } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { authTenantId } from '../../stores/auth-store.js'
import { gqlClient } from '../gql.js'
import { AdminInvoicesQuery, AdminInvoiceQuery, AdminCancelInvoiceMutation, AdminFiscalCustomersQuery, AdminCreateFiscalCustomerMutation, AdminUpdateFiscalCustomerMutation, AdminDeleteFiscalCustomerMutation } from '../queries/invoices.queries.js'
import * as M from '../mock-data.js'

const CFDI_SVC = 'dhk8-fiscal-cfdi'

// ── API DTOs ───────────────────────────────────────────────────────────────
interface ApiCustomer { id: string; tenantId: string; legalName: string; rfc?: string | null; type?: string; email?: string | null; regimeCode?: string | null; usoCfdi?: string | null }
interface ApiList<T> { items: T[]; total: number; page?: number; limit?: number }

// Nodo de factura tal como lo devuelve el grafo (campos = modelo db-erp).
interface GraphInvoice {
  id: string; tenantId: string; customerId: string; folio: string | null; status: string
  totalCents: number; paidCents: number; currency: string; issuedAt: string | null
  cfdiUuid: string | null; satMotivo: string | null; createdAt: string
}

function toUiStatus(s: string): M.Invoice['status'] {
  if (s === 'CANCELLED') return 'CANCELED'
  if (s === 'DRAFT') return 'PENDING'
  return 'STAMPED'
}
function toUiInvoiceFromGraph(i: GraphInvoice): M.Invoice {
  return {
    id: i.id, folio: i.folio ?? i.id, customer: i.customerId, rfc: '', totalCents: i.totalCents,
    status: toUiStatus(i.status), date: (i.issuedAt ?? i.createdAt).slice(0, 10), uuid: i.cfdiUuid ?? '',
  }
}
function toUiCustomer(c: ApiCustomer): M.FiscalCustomer {
  // F0.3: email/regimeCode/usoCfdi ya se persisten en el grafo (erp-customers).
  return { id: c.id, name: c.legalName, rfc: c.rfc ?? '', email: c.email ?? '', regimeCode: c.regimeCode ?? '', usoCfdi: c.usoCfdi ?? '' }
}

const db = { invoices: structuredClone(M.INVOICES), customers: structuredClone(M.FISCAL_CUSTOMERS) }
const id = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`
const tq = () => `tenantId=${encodeURIComponent(authTenantId())}`

export async function listInvoices(signal?: AbortSignal): Promise<M.Invoice[]> {
  if (PREVIEW_STUB) return mock(db.invoices)
  if (requireTenant() === null) return []
  // F2.4 / ADR-0009: lectura por el grafo (tenant del JWT, RLS). Escrituras siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminInvoicesQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.invoices.items ?? []).map(toUiInvoiceFromGraph)
}

export async function getInvoice(invId: string, signal?: AbortSignal): Promise<M.Invoice | null> {
  if (PREVIEW_STUB) return mock(db.invoices.find((x) => x.id === invId) ?? null)
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminInvoiceQuery, { id: invId }, ctx).toPromise()
  if (res.error) throw res.error
  const inv = res.data?.invoice
  return inv ? toUiInvoiceFromGraph(inv) : null
}

export async function cancelInvoice(invId: string, reason: string): Promise<M.Invoice> {
  if (PREVIEW_STUB) { const inv = db.invoices.find((x) => x.id === invId); if (inv) inv.status = 'CANCELED'; return mock(inv!) }
  // F9.9 / ADR-0008: primer leg (cancela la fila + registra satMotivo) por el grafo
  // (mutation cancelInvoice, tenant del JWT — IDOR-guard inherente). El SEGUNDO leg
  // (cancelación del CFDI ante el SAT vía fiscal-cfdi) SE QUEDA REST (zona reservada).
  const res = await gqlClient.mutation(AdminCancelInvoiceMutation, { id: invId, input: { satMotivo: reason || '02' } }).toPromise()
  if (res.error) throw res.error
  const inv = res.data?.cancelInvoice
  if (!inv) throw new Error('No se pudo cancelar la factura')
  if (inv.cfdiUuid) {
    try { await apiData<unknown>(svcUrl(CFDI_SVC, '/v1/cfdi/cancel'), { method: 'POST', body: { uuid: inv.cfdiUuid, motivo: '02' } }) } catch { /* CFDI may not be timbrado via this service */ }
  }
  return toUiInvoiceFromGraph(inv)
}

/** Generate the global invoice (factura global) for a period.
 *  TODO(backend): neither dhk8-erp-invoices nor dhk8-fiscal-cfdi expose a
 *  "global invoice" endpoint yet; this returns a synthesized row until they do. */
export async function generateGlobalInvoice(period: string): Promise<M.Invoice> {
  const inv: M.Invoice = { id: id('inv-glob'), folio: `G-${period}`, customer: `Factura global ${period}`, rfc: 'XAXX010101000', totalCents: M.mxnCents(12500 + Math.floor(Math.random() * 30000)), status: 'STAMPED', date: new Date().toISOString().slice(0, 10), uuid: id('UUID').toUpperCase() }
  if (PREVIEW_STUB) { db.invoices.unshift(inv); return mock(inv, 600) }
  return mock(inv, 300)
}

export async function listFiscalCustomers(signal?: AbortSignal): Promise<M.FiscalCustomer[]> {
  if (PREVIEW_STUB) return mock(db.customers)
  if (requireTenant() === null) return []
  // F2.5 / ADR-0009: lectura por el grafo (erp-customers, tenant del JWT, RLS).
  // Escrituras (create/update/delete) siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminFiscalCustomersQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.customers.items ?? []).map((c) => toUiCustomer(c))
}

export async function createFiscalCustomer(input: Omit<M.FiscalCustomer, 'id'>): Promise<M.FiscalCustomer> {
  if (PREVIEW_STUB) { const c = { ...input, id: id('fc') }; db.customers.push(c); return mock(c) }
  // F8.x / ADR-0009: escritura por el grafo (erp-customers, tenant del JWT, RLS).
  const gqlInput: { legalName: string; rfc?: string; type?: string; email?: string; regimeCode?: string; usoCfdi?: string } = { legalName: input.name, type: 'COMPANY' }
  if (input.rfc) gqlInput.rfc = input.rfc
  if (input.email) gqlInput.email = input.email
  if (input.regimeCode) gqlInput.regimeCode = input.regimeCode
  if (input.usoCfdi) gqlInput.usoCfdi = input.usoCfdi
  const res = await gqlClient.mutation(AdminCreateFiscalCustomerMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const c = res.data?.createCustomer
  if (!c) throw new Error('No se pudo crear el cliente fiscal')
  return toUiCustomer(c)
}

export async function updateFiscalCustomer(c: M.FiscalCustomer): Promise<M.FiscalCustomer> {
  if (PREVIEW_STUB) { const i = db.customers.findIndex((x) => x.id === c.id); if (i >= 0) db.customers[i] = c; return mock(c) }
  // F8.x / ADR-0009: escritura por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminUpdateFiscalCustomerMutation, { id: c.id, input: { legalName: c.name, rfc: c.rfc || null, email: c.email || null, regimeCode: c.regimeCode || null, usoCfdi: c.usoCfdi || null } }).toPromise()
  if (res.error) throw res.error
  const r = res.data?.updateCustomer
  if (!r) throw new Error('No se pudo actualizar el cliente fiscal')
  return toUiCustomer(r)
}

export async function deleteFiscalCustomer(cId: string): Promise<void> {
  if (PREVIEW_STUB) { db.customers = db.customers.filter((x) => x.id !== cId); return mock(undefined) }
  // F8.x / ADR-0009: soft-delete por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminDeleteFiscalCustomerMutation, { id: cId }).toPromise()
  if (res.error) throw res.error
}
